import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { Eye, ChevronLeft } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getUserRetreats } from '../../Services/Users/UserApi';
import Button from '@mui/material/Button';
import GlobalPagination from '../../Components/Common/Globalpagination';

const UserRetreats = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const userId = location.state?.userId;
    const userName = location.state?.userName;

    const [retreats, setRetreats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [limit, setLimit] = useState(25);

    const fetchUserRetreats = useCallback(async (currentPage = 1, currentLimit = limit) => {
        if (!userId) {
            setError("No user ID provided.");
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const response = await getUserRetreats(userId, currentPage, currentLimit);
            if (response && response.success) {
                setRetreats(response.data || []);
                setTotalItems(response.totalItems || 0);
                setTotalPages(response.totalPages || 1);
                setError(null);
            } else {
                setRetreats([]);
                setError("No retreats found for this user.");
            }
        } catch (err) {
            console.error(err);
            setError("Failed to fetch user retreats.");
            setRetreats([]);
        } finally {
            setLoading(false);
        }
    }, [userId, limit]);

    useEffect(() => {
        fetchUserRetreats(page);
    }, [fetchUserRetreats, page]);

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleViewDetails = (retreat) => {
        navigate('/retreat/view-details', { state: { retreat } });
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'published':
                return { bgcolor: '#E8F5E8', color: '#2E7D32' };
            case 'draft':
                return { bgcolor: '#FFF3E0', color: '#F57C00' };
            case 'rejected':
                return { bgcolor: '#FFEBEE', color: '#C62828' };
            default:
                return { bgcolor: '#F5F5F5', color: '#666' };
        }
    };

    const getEventTypeColor = (eventType) => {
        switch (eventType?.toLowerCase()) {
            case 'training':
                return { bgcolor: '#E3F2FD', color: '#1976D2' };
            case 'workshop':
                return { bgcolor: '#F3E5F5', color: '#7B1FA2' };
            case 'retreat':
                return { bgcolor: '#E8F5E8', color: '#2E7D32' };
            default:
                return { bgcolor: '#F5F5F5', color: '#666' };
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
            {/* Header */}
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 4 }}
            >
                <Stack direction="row" alignItems="center" spacing={2}>
                    <IconButton onClick={() => navigate(-1)} sx={{ color: '#222' }}>
                        <ChevronLeft size={24} />
                    </IconButton>
                    <Box>
                        <Typography
                            sx={{
                                color: '#222',
                                fontWeight: 500,
                                fontSize: { xs: '1.5rem', md: '1.8rem' },
                            }}
                        >
                            Retreats for {userName || 'User'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            User ID: {userId}
                        </Typography>
                    </Box>
                </Stack>

                <Button
                    variant="outlined"
                    onClick={() => navigate('/user')}
                    sx={{ borderRadius: '8px', textTransform: 'none' }}
                >
                    Back to Users
                </Button>
            </Stack>

            {/* Error */}
            {error && <Alert severity="info" sx={{ mb: 3 }}>{error}</Alert>}

            {/* Table */}
            <Paper
                sx={{
                    borderRadius: "8px",
                    border: "1px solid #E8E8E8",
                    overflow: "hidden"
                }}
                elevation={0}
            >
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap', textAlign: 'center' }}>S.No</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Title</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Event Type</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Status</TableCell>
                                {/* <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Step</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Completed</TableCell> */}
                                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Created Date</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Updated Date</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }} align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                                        <CircularProgress sx={{ color: '#FFBD65' }} />
                                    </TableCell>
                                </TableRow>
                            ) : retreats.length > 0 ? (
                                retreats.map((retreat, idx) => (
                                    <TableRow key={retreat.id} sx={{
                                        transition: 'background-color 0.2s',
                                        '& td': { borderBottom: '1px solid #F0F0F0' },
                                        '&:last-child td': { borderBottom: 0 }
                                    }}>
                                        <TableCell sx={{ color: '#444', py: 2.5, fontWeight: 500, textAlign: 'center' }}>{(page - 1) * limit + idx + 1}</TableCell>

                                        <TableCell sx={{ py: 2.5, fontWeight: 600, color: '#333', maxWidth: 250 }}>
                                            <Typography
                                                sx={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    fontSize: '0.875rem',
                                                    fontWeight: 600
                                                }}
                                                title={retreat.title}
                                            >
                                                {retreat.title || 'N/A'}
                                            </Typography>
                                        </TableCell>

                                        <TableCell sx={{ py: 2.5 }}>
                                            <Box
                                                sx={{
                                                    display: 'inline-block',
                                                    px: 1.5,
                                                    py: 0.5,
                                                    borderRadius: '4px',
                                                    bgcolor: getEventTypeColor(retreat.eventType).bgcolor,
                                                    color: getEventTypeColor(retreat.eventType).color,
                                                    fontSize: '0.7rem',
                                                    fontWeight: 600
                                                }}
                                            >
                                                {retreat.eventType || 'N/A'}
                                            </Box>
                                        </TableCell>

                                        <TableCell sx={{ py: 2.5 }}>
                                            <Box
                                                sx={{
                                                    display: 'inline-block',
                                                    px: 1.5,
                                                    py: 0.5,
                                                    borderRadius: '4px',
                                                    bgcolor: getStatusColor(retreat.status).bgcolor,
                                                    color: getStatusColor(retreat.status).color,
                                                    fontSize: '0.7rem',
                                                    fontWeight: 600
                                                }}
                                            >
                                                {retreat.status || 'N/A'}
                                            </Box>
                                        </TableCell>

                                        {/* <TableCell sx={{ py: 2.5 }}>
                                            <Typography sx={{ color: '#666', fontWeight: 500, fontSize: '0.875rem' }}>
                                                {retreat.completedStep ?? 'N/A'}
                                            </Typography>
                                        </TableCell>

                                        <TableCell sx={{ py: 2.5 }}>
                                            <Box
                                                sx={{
                                                    display: 'inline-block',
                                                    px: 1.5,
                                                    py: 0.5,
                                                    borderRadius: '4px',
                                                    bgcolor: retreat.isCompleted ? 'rgba(76, 175, 80, 0.1)' : 'rgba(102, 102, 102, 0.1)',
                                                    color: retreat.isCompleted ? '#2e7d32' : '#666',
                                                    fontSize: '0.7rem',
                                                    fontWeight: 600
                                                }}
                                            >
                                                {retreat.isCompleted ? 'Yes' : 'No'}
                                            </Box>
                                        </TableCell> */}

                                        <TableCell sx={{ py: 2.5 }}>
                                            <Typography sx={{ color: '#666', fontSize: '0.875rem' }}>
                                                {new Date(retreat.createdAt).toLocaleDateString()}
                                            </Typography>
                                        </TableCell>

                                        <TableCell sx={{ py: 2.5 }}>
                                            <Typography sx={{ color: '#666', fontSize: '0.875rem' }}>
                                                {new Date(retreat.updatedAt).toLocaleDateString()}
                                            </Typography>
                                        </TableCell>

                                        <TableCell align="right" sx={{ py: 2.5 }}>
                                            <Tooltip title="View Details">
                                                <IconButton
                                                    size="small"
                                                    sx={{ color: '#444' }}
                                                    onClick={() => handleViewDetails(retreat)}
                                                >
                                                    <Eye size={18} />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                                        No retreats found for this user.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Pagination */}
            <GlobalPagination
                page={page}
                pageSize={limit}
                totalRecords={totalItems}
                totalPages={totalPages}
                onPageChange={(newPage) => {
                    setPage(newPage);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                onPageSizeChange={(newSize) => {
                    setLimit(newSize);
                    setPage(1);
                }}
                loading={loading}
            />
        </Box>
    );
};

export default UserRetreats;
